import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Lock } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { MEMBER_PASSWORD_RULE_LABEL, validatePassword } from '../utils/memberUtils';
import { AUTH_LIGHT_SCREEN_BG, useBodyBackgroundOnMount } from '../hooks/useBodyBackgroundOnMount';
import { requiresPasswordSetup } from '../lib/passwordSetup';
import type { Session } from '@supabase/supabase-js';

export default function SetupPassword() {
  useBodyBackgroundOnMount(AUTH_LIGHT_SCREEN_BG);

  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [hasInviteSession, setHasInviteSession] = useState<boolean | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const syncInviteSession = (session: Session | null) => {
      if (cancelled) return;
      const user = session?.user ?? null;
      if (!user) {
        setHasInviteSession(false);
        return;
      }

      if (!requiresPasswordSetup(user)) {
        navigate('/account');
        return;
      }

      setHasInviteSession(true);
    };

    const check = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        syncInviteSession(data.session);
      } catch {
        if (cancelled) return;
        setHasInviteSession(false);
      }
    };

    check();
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      syncInviteSession(session);
    });

    return () => {
      cancelled = true;
      try {
        data.subscription.unsubscribe();
      } catch {
        // noop
      }
    };
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.valid) {
      setFormError(passwordValidation.error ?? 'パスワードが無効です。');
      return;
    }

    if (newPassword !== confirmPassword) {
      setFormError('パスワードが一致しません。');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
        data: {
          requires_password_setup: false,
          personal_login_setup_completed: true,
        },
      });
      if (error) throw error;

      setSuccess(true);
      window.setTimeout(() => {
        navigate('/account');
      }, 1500);
    } catch (error: any) {
      setFormError(error?.message || 'パスワード設定に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  if (hasInviteSession === null && !success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-teal-600">Handra</CardTitle>
            <CardDescription>パスワード設定</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-gray-500">認証情報を確認中です...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (hasInviteSession === false) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-teal-600">Handra</CardTitle>
            <CardDescription>パスワード設定</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-center">
              <p className="text-gray-700">招待リンクが無効、または期限切れです。</p>
              <p className="text-sm text-gray-500">
                最新の招待メールに記載されたリンクを開いてください。
              </p>
              <div className="pt-4">
                <Link to="/login">
                  <Button variant="cta" className="w-full">
                    ログイン画面へ
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-teal-600">Handra</CardTitle>
            <CardDescription>パスワード設定完了</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-center">
              <p className="text-gray-700">パスワードを設定しました。</p>
              <p className="text-sm text-gray-500">このままアプリへ移動します。</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-teal-600">Handra</CardTitle>
          <CardDescription>新しいパスワードを設定</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-gray-700">
              初回ログインのため、続けてパスワードを設定してください。
            </p>

            <div className="space-y-2">
              <Label htmlFor="new-password">新しいパスワード（{MEMBER_PASSWORD_RULE_LABEL}）</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="new-password"
                  type="password"
                  placeholder="新しいパスワードを入力"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="pl-10 placeholder:text-gray-300"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">パスワード（確認）</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="パスワードを再入力"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 placeholder:text-gray-300"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {formError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {formError}
              </p>
            )}

            <div className="pt-2">
              <Button type="submit" variant="cta" className="w-full" disabled={loading}>
                {loading ? '設定中...' : 'パスワードを設定する'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
